import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuthenticator } from '@aws-amplify/ui-react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import LanguageIcon from '@mui/icons-material/Language';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { format } from 'date-fns';

import CustomBackdrop from '../../components/CustomBackdrop/CustomBackdrop';
import KeywordSelectionPopover from '../../components/KeywordSelectionPopover';
import PageHeader from '../../components/PageHeader';
import SearchComponent from '../../components/Search/Search';
import {
  useAcquireValidationLockMutation,
  useCompanyInboxListLazyQuery,
  useCrawlerInboxLazyQuery,
  useGetActiveRagValidationsLazyQuery,
  useGetBuyAndBuildPlatformsLazyQuery,
  useGetCompanyLazyQuery,
  useGetRagJobStatusLazyQuery,
  useGetShortCompanyLazyQuery,
  useMarkCompanyAsNotSoftwareMutation,
  useStartCrawlMutation,
  useStartVerificationSessionMutation,
  useValidateCompanyWithRagMutation,
  useValidationInboxLazyQuery,
} from '../../gql/generated/graphql';
import { handleVisitPage } from '../../helpers/helpers';
import { useCompanyNavigation } from '../../hooks/useCompanyNavigation';
import { useSnackbar } from '../../providers/SnackbarProvider';
import { paths } from '../../routes/paths';
import CustomPaper from '../DFMPage/components/CustomPaper/CustomPaper';

import ActionButtonsFalseNegatives from './components/ActionButtonsFalseNegatives';
import { countryOptions } from './helpers';
import { TabIndex } from './types';
import { countryCodeToFlag, formatDateOnly, getCallabilityChipProps, getCompanyLogo, getFteChipProps } from './utils';

// Custom hook for SIC justification tooltip
const useSicJustificationTooltip = () => {
  const [hoveredCompany, setHoveredCompany] = useState<{ id: number; justification?: string } | null>(null);
  const [getCompany, { data: companyData }] = useGetCompanyLazyQuery();

  const handleMouseEnter = (companyId: number) => {
    getCompany({ variables: { companyId } });
    setHoveredCompany({ id: companyId });
  };

  const handleMouseLeave = () => {
    setHoveredCompany(null);
  };

  // Extract justification from company data
  const justification = companyData?.getCompany?.edges?.[0]?.node?.sicCodeJustification;

  return {
    hoveredCompany,
    justification,
    handleMouseEnter,
    handleMouseLeave,
  };
};

// Row type can be either CompanyInboxType (legacy MV) or CrawlerInboxItemType (new table)
// Using `any` for DataGrid row since the grid handles both data shapes
type InboxRow = any;

const CrawlerInbox = () => {
  const navigate = useNavigate();
  const { handleValidate, handleValidateV2, handleNavigateToAssesment } = useCompanyNavigation();
  const { hoveredCompany, justification, handleMouseEnter, handleMouseLeave } = useSicJustificationTooltip();
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const { user } = useAuthenticator();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [platform, setPlatform] = useState<string | null>(null);

  // URL Preview Popover state
  const [previewAnchor, setPreviewAnchor] = useState<HTMLElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const isPreviewOpen = Boolean(previewAnchor);

  const companyParamId = searchParams.get('companyId');

  const [getShortCompany, { data: shortCompanyData, loading: shortCompanyLoading }] = useGetShortCompanyLazyQuery();

  useEffect(() => {
    if (!companyParamId) return;
    getShortCompany({ variables: { companyId: Number(companyParamId) } });
  }, [companyParamId]);

  const [
    getCompanyInboxList,
    { data: companyInboxList, loading: companyInboxListLoading, refetch: companyInboxListRefetch },
  ] = useCompanyInboxListLazyQuery();

  // Event-driven crawler inbox query (replaces MV-backed companyInboxList for Country tab)
  const [getCrawlerInbox, { data: crawlerInboxData, loading: crawlerInboxLoading, refetch: crawlerInboxRefetch }] =
    useCrawlerInboxLazyQuery();

  const [getBuyAndBuildPlatforms, { data: getBuyAndBuildPlatformsData }] = useGetBuyAndBuildPlatformsLazyQuery();

  const [markCompanyAsNotSoftware, { loading: markCompanyAsNotSoftwareLoading }] =
    useMarkCompanyAsNotSoftwareMutation();

  const [startCrawl, { loading: startCrawlLoading }] = useStartCrawlMutation();

  // Validation V2 inbox
  const [
    getValidationInbox,
    { data: validationInboxData, loading: validationInboxLoading, refetch: validationInboxRefetch },
  ] = useValidationInboxLazyQuery();
  const [acquireValidationLock] = useAcquireValidationLockMutation();

  const companyName = shortCompanyData?.getCompany?.edges?.[0]?.node?.shortName;

  const getBuyAndBuildPlatformsNode = getBuyAndBuildPlatformsData?.getBuyAndBuildPlatforms?.edges?.map(
    edge => edge?.node
  );

  const [startVerificationSession] = useStartVerificationSessionMutation();

  // RAG Validation state and hooks
  const [validateCompanyWithRag] = useValidateCompanyWithRagMutation();
  const [getRagJobStatus] = useGetRagJobStatusLazyQuery();
  const [getActiveRagValidations] = useGetActiveRagValidationsLazyQuery();
  const [ragValidationJobs, setRagValidationJobs] = useState<
    Record<string, { jobId: string; progress: number; status: string; message?: string }>
  >({});
  const ragPollingIntervals = useRef<Record<string, NodeJS.Timeout>>({});
  const activeValidationsPollingRef = useRef<NodeJS.Timeout | null>(null);
  // Ref to track current jobs state for use in callbacks (avoids stale closures)
  const ragValidationJobsRef = useRef(ragValidationJobs);

  // Keep the ref in sync with state
  useEffect(() => {
    ragValidationJobsRef.current = ragValidationJobs;
  }, [ragValidationJobs]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      Object.values(ragPollingIntervals.current).forEach(clearInterval);
      if (activeValidationsPollingRef.current) {
        clearInterval(activeValidationsPollingRef.current);
      }
    };
  }, []);

  // Poll for RAG job status
  const pollRagJobStatus = useCallback(
    async (domain: string, jobId: string) => {
      // Guard against missing jobId
      if (!jobId) {
        console.warn(`pollRagJobStatus called with empty jobId for ${domain}`);
        return;
      }

      try {
        const result = await getRagJobStatus({
          variables: { jobId },
          fetchPolicy: 'network-only',
        });

        const status = result.data?.ragJobStatus;
        if (!status) return;

        setRagValidationJobs(prev => ({
          ...prev,
          [domain]: {
            jobId,
            progress: status.progress?.percent || 0,
            status: status.status,
            message: status.progress?.message || 'Processing...',
          },
        }));

        if (status.status === 'complete') {
          if (ragPollingIntervals.current[domain]) {
            clearInterval(ragPollingIntervals.current[domain]);
            delete ragPollingIntervals.current[domain];
          }
          showSnackbar(`RAG validation complete for ${domain}`, 'success');
          // Refetch inbox to get updated data
          companyInboxListRefetch?.();
          crawlerInboxRefetch?.();
          // Clear from jobs after a delay
          setTimeout(() => {
            setRagValidationJobs(prev => {
              const newJobs = { ...prev };
              delete newJobs[domain];
              return newJobs;
            });
          }, 2000);
        } else if (status.status === 'failed') {
          if (ragPollingIntervals.current[domain]) {
            clearInterval(ragPollingIntervals.current[domain]);
            delete ragPollingIntervals.current[domain];
          }
          showSnackbar(`RAG validation failed: ${status.error || 'Unknown error'}`, 'error');
          setRagValidationJobs(prev => {
            const newJobs = { ...prev };
            delete newJobs[domain];
            return newJobs;
          });
        }
      } catch (err) {
        console.error('Error polling RAG job status:', err);
      }
    },
    [getRagJobStatus, showSnackbar, companyInboxListRefetch]
  );

  // Handle RAG validation trigger
  const handleRagValidation = useCallback(
    async (domain: string) => {
      if (!domain || ragValidationJobs[domain]) return;

      try {
        const result = await validateCompanyWithRag({
          variables: { companyDomain: domain, shadowMode: false },
        });

        const response = result.data?.validateCompanyWithRag;
        const jobId = response?.jobId;

        if (!jobId) {
          showSnackbar('Failed to start RAG validation', 'error');
          return;
        }

        // Check if validation was already in progress (started by another user)
        if (!response?.success && jobId) {
          showSnackbar('Validation already in progress - tracking existing job', 'info');
        }

        // Initialize tracking state (works for both new and existing jobs)
        setRagValidationJobs(prev => ({
          ...prev,
          [domain]: { jobId, progress: 0, status: 'pending', message: 'Starting...' },
        }));

        // Start polling
        ragPollingIntervals.current[domain] = setInterval(() => {
          pollRagJobStatus(domain, jobId);
        }, 2000);

        // Initial poll
        pollRagJobStatus(domain, jobId);
      } catch (err) {
        console.error('Error starting RAG validation:', err);
        showSnackbar('Error starting RAG validation', 'error');
      }
    },
    [validateCompanyWithRag, ragValidationJobs, pollRagJobStatus, showSnackbar]
  );

  // Check for active RAG validations (started by other users or system)
  const checkActiveValidations = useCallback(async () => {
    const companies = [...(companyInboxList?.companyInboxList || []), ...(crawlerInboxData?.crawlerInbox || [])];
    // Use ref to get current jobs state (avoids stale closures and unnecessary re-renders)
    const currentJobs = ragValidationJobsRef.current;
    const domains = companies.map(c => c?.uniqueDomain).filter((d): d is string => Boolean(d) && !currentJobs[d]);

    if (domains.length === 0) return;

    try {
      const result = await getActiveRagValidations({
        variables: { companyDomains: domains },
        fetchPolicy: 'network-only',
      });

      const activeValidationsJson = result.data?.activeRagValidations;
      if (!activeValidationsJson) return;

      const activeValidations = JSON.parse(activeValidationsJson) as Record<
        string,
        { jobId: string; status: string; progress?: { percent?: number; message?: string } }
      >;

      // Start tracking any active validations we're not already tracking
      Object.entries(activeValidations).forEach(([domain, validation]) => {
        // Use ref for current check to avoid stale closures
        if (!ragValidationJobsRef.current[domain] && !ragPollingIntervals.current[domain]) {
          const jobId = validation.jobId;

          // Guard against missing jobId
          if (!jobId) {
            console.warn(`Active validation for ${domain} has no jobId, skipping`);
            return;
          }

          // Initialize tracking state
          setRagValidationJobs(prev => ({
            ...prev,
            [domain]: {
              jobId,
              progress: validation.progress?.percent || 0,
              status: validation.status,
              message: validation.progress?.message || 'Processing...',
            },
          }));

          // Start polling for this job
          ragPollingIntervals.current[domain] = setInterval(() => {
            pollRagJobStatus(domain, jobId);
          }, 2000);
        }
      });
    } catch (err) {
      console.error('Error checking active RAG validations:', err);
    }
  }, [companyInboxList, crawlerInboxData, getActiveRagValidations, pollRagJobStatus]);

  // Check for active validations when companies load and periodically (every 10 seconds)
  useEffect(() => {
    const hasData = companyInboxList?.companyInboxList?.length || crawlerInboxData?.crawlerInbox?.length;
    if (hasData) {
      checkActiveValidations();

      if (activeValidationsPollingRef.current) {
        clearInterval(activeValidationsPollingRef.current);
      }
      activeValidationsPollingRef.current = setInterval(checkActiveValidations, 10000);
    }

    return () => {
      if (activeValidationsPollingRef.current) {
        clearInterval(activeValidationsPollingRef.current);
      }
    };
  }, [companyInboxList, crawlerInboxData, checkActiveValidations]);

  useEffect(() => {
    if (activeTab === TabIndex.PORTFOLIO) {
      getBuyAndBuildPlatforms();
    }
    if (activeTab === TabIndex.VALIDATION) {
      getValidationInbox({ fetchPolicy: 'network-only' });
    }
  }, [activeTab]);

  useEffect(() => {
    switch (activeTab) {
      case TabIndex.COUNTRY:
        // Handled by handleOptionSelect / initial useEffect
        break;
      case TabIndex.PORTFOLIO:
        if (platform) {
          getCrawlerInbox({
            variables: { platformId: Number(platform), limit: 200 },
            fetchPolicy: 'network-only',
          });
        }
        break;
      case TabIndex.POTENTIAL_PLATFORMS:
        getCompanyInboxList({
          variables: { newPlatforms: true, country: countryOptions[0].value },
          fetchPolicy: 'network-only',
        });
        break;
      case TabIndex.STEALTH_PLATFORMS:
        getCompanyInboxList({
          variables: { prioritizedPlatforms: true },
          fetchPolicy: 'network-only',
        });
        break;
      case TabIndex.FALSE_NEGATIVES:
        getCompanyInboxList({
          variables: { falseNegatives: true },
          fetchPolicy: 'network-only',
        });
        break;
    }
  }, [activeTab, platform]);

  // URL Preview handlers
  const handlePreviewMouseEnter = (event: React.MouseEvent<HTMLElement>, url: string) => {
    console.log('handlePreviewMouseEnter called with URL:', url);

    // Capture the element reference before setTimeout (React event pooling)
    const targetElement = event.currentTarget;

    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }

    // Set a new timeout for 2 seconds
    const timeout = setTimeout(() => {
      console.log('2 second timeout completed, opening preview for:', url);
      console.log('Target element:', targetElement);
      setPreviewAnchor(targetElement);
      // Ensure URL has protocol
      const formattedUrl = url && !url.startsWith('http') ? `https://${url}` : url;
      console.log('Formatted URL:', formattedUrl);
      setPreviewUrl(formattedUrl);
    }, 2000);

    setHoverTimeout(timeout);
  };

  const handlePreviewMouseLeave = () => {
    // Clear timeout if user moves mouse away before 2 seconds
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  const handlePreviewClose = () => {
    setPreviewAnchor(null);
    setPreviewUrl('');
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  const productCategoryColumns: GridColDef[] = [
    {
      field: 'logo',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: params => {
        const logoUrl = params.row?.logoUrl;
        const companyName = params.row?.shortName || params.row?.uniqueDomain;
        const companyId = params.row?.companyId ?? params.row?.dbId;
        const isCurrentlyHovered = hoveredCompany?.id === companyId;

        const companyUrl = params.row?.crawlUrl || params.row?.uniqueDomain;

        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
            height="100%"
            sx={{
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.7,
              },
            }}
            onClick={() => handleValidateAction(params.row)}
            onMouseEnter={e => {
              console.log('🔥 Logo cell mouseEnter:', { companyId, companyUrl });
              if (companyId) {
                handleMouseEnter(companyId);
              }
              if (companyUrl) {
                handlePreviewMouseEnter(e, companyUrl);
              }
            }}
            onMouseLeave={() => {
              console.log('🔥 Logo cell mouseLeave');
              handleMouseLeave();
              handlePreviewMouseLeave();
            }}
          >
            <Tooltip
              title={
                isCurrentlyHovered && justification ? (
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                      SIC Code Justification:
                    </Typography>
                    <Typography variant="body2">{justification}</Typography>
                  </Box>
                ) : (
                  ''
                )
              }
              arrow
              placement="right"
              enterDelay={500}
              slotProps={{
                popper: {
                  sx: {
                    pointerEvents: 'none',
                  },
                },
              }}
            >
              <Box sx={{ pointerEvents: 'none' }}>{getCompanyLogo(logoUrl, companyName)}</Box>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'uniqueDomain',
      headerName: 'Name',
      minWidth: 150,
      flex: 1,
      sortable: true,
      renderCell: params => {
        const companyName = params.row?.shortName;
        const domain = params.value;
        const crawlUrl = params.row?.crawlUrl;

        // Ensure crawlUrl has a protocol
        const fixedCrawlUrl = crawlUrl && !crawlUrl.startsWith('http') ? `https://${crawlUrl}` : crawlUrl;

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
            {companyName && (
              <Typography
                variant="body2"
                fontWeight={500}
                component={fixedCrawlUrl ? 'a' : 'span'}
                href={fixedCrawlUrl || undefined}
                target={fixedCrawlUrl ? '_blank' : undefined}
                rel={fixedCrawlUrl ? 'noopener noreferrer' : undefined}
                sx={{
                  color: fixedCrawlUrl ? 'primary.main' : 'inherit',
                  textDecoration: fixedCrawlUrl ? 'underline' : 'none',
                  cursor: fixedCrawlUrl ? 'pointer' : 'default',
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                  '&:hover': {
                    color: fixedCrawlUrl ? 'primary.dark' : 'inherit',
                  },
                }}
              >
                {companyName}
              </Typography>
            )}
            {domain && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  lineHeight: 1,
                  wordBreak: 'break-word',
                  fontSize: '0.75rem',
                }}
              >
                {domain}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'country',
      headerName: t('validation.finishStep.country'),
      width: 80,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderCell: params => {
        const countryCode = params.value;
        const flag = countryCodeToFlag(countryCode);

        return (
          <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
            <Typography variant="body2">{flag}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'fte',
      headerName: t('validation.finishStep.fte'),
      width: 100,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderCell: params => {
        const fteValue = params.value;
        const chipProps = getFteChipProps(fteValue);

        return (
          <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100%">
            <Chip size="small" {...chipProps} />
          </Box>
        );
      },
    },
    {
      field: 'topRelatedCompanyName',
      width: 160,
      headerName: 'BID Platform',
      description: 'Portfolio company with the highest buy-and-build relevance score for this target',
      sortable: true,
      headerAlign: 'center',
      align: 'center',
      renderCell: params => {
        const companyName = params?.formattedValue;
        const logoUrl = params?.row?.topRelatedCompanyLogoUrl;
        if (companyName) {
          return (
            <Box display="flex" alignItems="center" gap={1} justifyContent="center">
              {getCompanyLogo(logoUrl, companyName)}
              <Typography variant="body2" noWrap title={companyName}>
                {companyName}
              </Typography>
            </Box>
          );
        } else {
          return (
            <Typography variant="body2" sx={{ color: 'grey.400' }}>
              -
            </Typography>
          );
        }
      },
    },
    {
      field: 'systemType',
      headerName: 'SW Category',
      description: 'Software category from SIC code classification (e.g. ERP, CRM, HRM)',
      width: 180,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderCell: params => {
        const systemType = params.row?.systemType || '';
        const justificationText = params.row?.sicCodeJustification || '';

        if (justificationText) {
          return (
            <Tooltip
              title={
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    SIC Code Justification:
                  </Typography>
                  <Typography variant="body2">{justificationText}</Typography>
                </Box>
              }
              arrow
              placement="top"
            >
              <Box
                sx={{
                  width: '100%',
                  textAlign: 'center',
                  cursor: 'help',
                }}
              >
                <Typography variant="body2">{systemType}</Typography>
              </Box>
            </Tooltip>
          );
        } else {
          return (
            <Box
              sx={{
                width: '100%',
                textAlign: 'center',
              }}
            >
              <Typography variant="body2">{systemType}</Typography>
            </Box>
          );
        }
      },
    },
    {
      field: 'productName',
      headerName: 'Product',
      description: 'Top product categorization by certainty score',
      width: 170,
      sortable: true,
      renderCell: params => (
        <Typography variant="body2" noWrap title={params.value || ''}>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'customerVertical',
      headerName: 'Customer',
      description: 'Most common customer industry vertical from reference analysis',
      width: 160,
      sortable: true,
      renderCell: params => (
        <Typography variant="body2" noWrap title={params.value || ''}>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'aiValidationScore',
      width: 120,
      headerName: 'AI Validation',
      description: 'RAG-based validation progress: customers (33%), keywords (33%), company size (34%)',
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderCell: params => {
        const domain = params.row?.uniqueDomain;
        const ragJob = domain ? ragValidationJobs[domain] : null;

        // If RAG validation is in progress, show that progress instead
        if (ragJob && ragJob.status !== 'complete') {
          const ragPercentage = ragJob.progress || 0;
          const coralColor = 'var(--brand-coral)';
          const coralBgColor = 'rgba(255, 86, 79, 0.1)';

          return (
            <Tooltip title={ragJob.message || 'Validating...'}>
              <Box
                display="flex"
                alignItems="center"
                width="100%"
                height="100%"
                sx={{
                  backgroundColor: coralBgColor,
                  borderRadius: '4px',
                  padding: '2px 8px',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      height: 6,
                      backgroundColor: 'var(--border-subtle)',
                      borderRadius: '3px',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${ragPercentage}%`,
                        height: '100%',
                        backgroundColor: coralColor,
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: '35px',
                      color: coralColor,
                      fontWeight: 'bold',
                    }}
                  >
                    {ragPercentage}%
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          );
        }

        // Normal display: show stored AI validation score
        const score = params?.value || 0;
        const percentage = Math.round(score * 100);
        let color = 'var(--border-subtle)'; // gray for 0%
        let bgColor = 'rgba(224, 224, 224, 0.1)';

        if (score >= 1.0) {
          color = '#4caf50'; // green for 100%
          bgColor = 'rgba(76, 175, 80, 0.1)';
        } else if (score >= 0.666) {
          color = '#ff9800'; // orange for ~66%
          bgColor = 'rgba(255, 152, 0, 0.1)';
        } else if (score >= 0.333) {
          color = '#ff5722'; // deep orange for ~33%
          bgColor = 'rgba(255, 87, 34, 0.1)';
        }

        return (
          <Box
            display="flex"
            alignItems="center"
            width="100%"
            height="100%"
            sx={{
              backgroundColor: bgColor,
              borderRadius: '4px',
              padding: '2px 8px',
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  height: 6,
                  backgroundColor: 'var(--border-subtle)',
                  borderRadius: '3px',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${percentage}%`,
                    height: '100%',
                    backgroundColor: color,
                    transition: 'width 0.3s ease',
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  minWidth: '35px',
                  color: color,
                  fontWeight: 'bold',
                }}
              >
                {percentage}%
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'callabilityScore',
      headerName: 'Score',
      description: 'Callability score: weighted composite of data completeness, crawl quality, and enrichment coverage',
      width: 80,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderCell: params => {
        const chipProps = getCallabilityChipProps(params.value);
        return (
          <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100%">
            <Chip size="small" {...chipProps} />
          </Box>
        );
      },
    },
    {
      field: 'datetimeCreated',
      headerName: 'First Seen',
      valueGetter: (value, row) => formatDateOnly(row?.companyCreatedAt || value),
      width: 120,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
    },
    {
      field: 'actions',
      headerName: '',
      editable: false,
      sortable: false,
      width: 200,
      renderCell: params => {
        return activeTab === TabIndex.FALSE_NEGATIVES ? (
          <ActionButtonsFalseNegatives
            params={params}
            onMarkAsNotSoftware={handleMarkAsNotSoftware}
            onForceCrawl={handleForceCrawl}
          />
        ) : (
          <>
            <IconButton size="small" onClick={() => handleValidateAction(params.row)}>
              <Tooltip title={t('validation.finishStep.validate')}>
                <CheckCircleOutlineIcon fontSize="small" />
              </Tooltip>
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleNavigateToAssesment(params.row?.companyId ?? params.row?.dbId)}
            >
              <Tooltip title={t('validation.finishStep.check')}>
                <FactCheckIcon fontSize="small" />
              </Tooltip>
            </IconButton>
            <KeywordSelectionPopover
              companyId={params.row?.companyId ?? params.row?.dbId}
              validationType="not_software"
              onConfirm={() => handleMarkAsNotSoftware(params.row)}
            >
              <IconButton size="small">
                <Tooltip title={t('validation.finishStep.notSW')}>
                  <CloseIcon fontSize="small" />
                </Tooltip>
              </IconButton>
            </KeywordSelectionPopover>
            <IconButton size="small" onClick={() => handleVisitPage(params.row.uniqueDomain)}>
              <LanguageIcon fontSize="small" />
            </IconButton>
            {ragValidationJobs[params.row.uniqueDomain] ? (
              <Tooltip
                title={`${ragValidationJobs[params.row.uniqueDomain].message} (${ragValidationJobs[params.row.uniqueDomain].progress}%)`}
              >
                <Box sx={{ position: 'relative', display: 'inline-flex', ml: 0.5 }}>
                  <CircularProgress
                    size={20}
                    variant={ragValidationJobs[params.row.uniqueDomain].progress > 0 ? 'determinate' : 'indeterminate'}
                    value={ragValidationJobs[params.row.uniqueDomain].progress}
                    sx={{ color: 'var(--brand-coral)' }}
                  />
                </Box>
              </Tooltip>
            ) : (
              <IconButton
                size="small"
                onClick={() => handleRagValidation(params.row.uniqueDomain)}
                disabled={!params.row.uniqueDomain}
              >
                <Tooltip title="AI validate">
                  <SmartToyIcon fontSize="small" />
                </Tooltip>
              </IconButton>
            )}
          </>
        );
      },
    },
  ];

  useEffect(() => {
    handleOptionSelect(null, countryOptions[0]);
  }, []);

  const handleOptionSelect = (_, option: { value: string; label: string }) => {
    if (option === null || option.value === '') return;
    switch (activeTab) {
      case TabIndex.COUNTRY:
        getCrawlerInbox({
          variables: { country: option.value, limit: 200 },
          fetchPolicy: 'network-only',
        });
        break;
      case TabIndex.POTENTIAL_PLATFORMS:
        getCompanyInboxList({
          variables: { country: option.value, newPlatforms: true },
          fetchPolicy: 'network-only',
        });
        break;
      default:
        return;
    }
  };

  const handleInputChange = (_, value: string) => {
    setSearchTerm(value);
  };
  const handleReset = () => {
    setSearchTerm('');
  };

  const getLabel = (option: { label: string; value: string }) => {
    return option?.label;
  };

  const handleMoveToValidation = () => {
    if (!companyParamId) return;
    handleValidateV2(parseInt(companyParamId), 'company');
  };

  const authId = user.username.replace('GoogleWorkspace_', '');

  // Determine if we're using the new crawler_inbox query for the current tab
  const useNewInbox = activeTab === TabIndex.COUNTRY || activeTab === TabIndex.PORTFOLIO;
  const activeInboxData = useNewInbox ? crawlerInboxData?.crawlerInbox : companyInboxList?.companyInboxList;
  const activeInboxLoading = useNewInbox ? crawlerInboxLoading : companyInboxListLoading;
  const activeInboxRefetch = useNewInbox ? crawlerInboxRefetch : companyInboxListRefetch;

  const handleValidateAction = async row => {
    const companyId = row?.companyId ?? row?.dbId;

    const result = await startVerificationSession({ variables: { companyId, authId } });
    if (result?.data?.startCompanyVerificationSession?.success) {
      handleValidateV2(companyId, 'company');
    } else {
      showSnackbar('VerificationSession', 'error');
    }
  };

  const handleMarkAsNotSoftware = async row => {
    const companyId = row?.companyId ?? row?.dbId;
    const result = await markCompanyAsNotSoftware({
      variables: { companyId },
    });
    if (result?.data?.markCompanyAsNotSoftware?.ok) {
      activeInboxRefetch?.();
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChangePlatform = (event: SelectChangeEvent) => {
    setPlatform(event.target.value);
  };

  const handleValidationInboxAction = async (row: any) => {
    const cId = row?.companyId;
    const track = row?.validationNeeded === 'company' ? 'company' : 'crawl';
    const crId = row?.latestCrawlResultId;

    // Attempt to acquire lock
    try {
      const result = await acquireValidationLock({
        variables: { companyId: cId, userId: 1 }, // userId will be resolved from auth
      });
      if (result.data?.acquireValidationLock?.success) {
        handleValidateV2(cId, track as 'company' | 'crawl', crId);
      } else {
        showSnackbar(result.data?.acquireValidationLock?.message || 'Could not acquire lock', 'warning');
      }
    } catch (err) {
      showSnackbar('Error acquiring validation lock', 'error');
    }
  };

  const handleForceCrawl = async row => {
    const result = await startCrawl({
      variables: {
        companyName: row?.shortName,
        domain: row?.uniqueDomain,
        forceSw: true,
      },
    });
    if (result?.data?.startCrawler?.success) {
      activeInboxRefetch?.();
    }
  };

  const validationInboxColumns: GridColDef[] = [
    {
      field: 'logoUrl',
      headerName: '',
      width: 50,
      sortable: false,
      renderCell: params => (
        <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
          {getCompanyLogo(params.value, params.row?.shortName)}
        </Box>
      ),
    },
    {
      field: 'shortName',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      renderCell: params => (
        <Box
          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => handleValidationInboxAction(params.row)}
        >
          <Typography variant="body2" fontWeight={500}>
            {params.value || params.row?.uniqueDomain}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row?.uniqueDomain}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'country',
      headerName: 'Country',
      width: 70,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderCell: params => <Typography variant="body2">{countryCodeToFlag(params.value)}</Typography>,
    },
    {
      field: 'analysisFte',
      headerName: 'FTE',
      width: 80,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderCell: params => <Chip size="small" {...getFteChipProps(params.value)} />,
    },
    {
      field: 'companyValidated',
      headerName: 'Company',
      width: 90,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderCell: params => (
        <Chip
          size="small"
          icon={params.value ? <DoneIcon /> : undefined}
          label={params.value ? 'Done' : 'Pending'}
          color={params.value ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'validationNeeded',
      headerName: 'Track',
      width: 100,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderCell: params => (
        <Chip
          size="small"
          label={params.value === 'company' ? 'Company' : 'Crawl'}
          color={params.value === 'company' ? 'primary' : 'secondary'}
        />
      ),
    },
    {
      field: 'companyCreatedAt',
      headerName: 'Created',
      width: 90,
      align: 'center' as const,
      headerAlign: 'center' as const,
      valueGetter: value => formatDateOnly(value),
    },
    {
      field: 'lastValidatedCrCreated',
      headerName: 'Last Val.',
      width: 90,
      align: 'center' as const,
      headerAlign: 'center' as const,
      valueGetter: value => formatDateOnly(value),
    },
    {
      field: 'latestCrCreated',
      headerName: 'Crawl',
      width: 90,
      align: 'center' as const,
      headerAlign: 'center' as const,
      valueGetter: value => formatDateOnly(value),
    },
    {
      field: 'callabilityScore',
      headerName: 'Score',
      width: 70,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderCell: params => {
        const score = params.value;
        if (score == null) return '-';
        return <Typography variant="caption">{Math.round(score * 100)}%</Typography>;
      },
    },
    {
      field: 'productName',
      headerName: 'Product',
      width: 130,
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: params => {
        const lockedBy = params.row?.validationLockedBy;
        if (lockedBy) {
          return (
            <Tooltip title={`Being validated by user #${lockedBy}`}>
              <Typography variant="caption" color="text.disabled">
                Locked
              </Typography>
            </Tooltip>
          );
        }
        return (
          <IconButton size="small" onClick={() => handleValidationInboxAction(params.row)}>
            <Tooltip title="Validate">
              <CheckCircleOutlineIcon fontSize="small" />
            </Tooltip>
          </IconButton>
        );
      },
    },
  ];

  const isActiveFalseNegatives = activeTab === TabIndex.FALSE_NEGATIVES;

  return (
    <Box>
      <PageHeader
        title="Crawler Inbox"
        subtitle="Review and validate incoming company data"
        variant="tabs"
        tabs={[
          { label: t('validation.tabs.country'), value: TabIndex.COUNTRY },
          { label: t('validation.tabs.portfolio'), value: TabIndex.PORTFOLIO },
          { label: t('validation.tabs.potentialPlatforms'), value: TabIndex.POTENTIAL_PLATFORMS },
          { label: t('validation.tabs.stealthPlatforms'), value: TabIndex.STEALTH_PLATFORMS },
          { label: t('validation.tabs.falseNegatives'), value: TabIndex.FALSE_NEGATIVES },
          { label: 'Validation', value: TabIndex.VALIDATION },
        ]}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />
      <Box px={3} py={2} display="flex" gap={2} flexDirection="column">
        {activeTab === TabIndex.COUNTRY || activeTab === TabIndex.POTENTIAL_PLATFORMS ? (
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography
              variant="body2"
              sx={{
                fontSize: 'var(--text-sm)',
                fontFamily: 'var(--font-data)',
                color: 'var(--text-secondary)',
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}
            >
              {t('validation.countrySelectLabel')}
            </Typography>
            <Box sx={{ minWidth: '200px', flex: 1, maxWidth: '400px' }}>
              <SearchComponent
                options={countryOptions}
                loading={false}
                searchTerm={searchTerm}
                defaultValue
                onInputChange={handleInputChange}
                onOptionSelect={handleOptionSelect}
                onReset={handleReset}
                getCustomOptionLabel={getLabel}
              />
            </Box>
          </Box>
        ) : null}
        {activeTab === TabIndex.PORTFOLIO && (
          <Box sx={{ maxWidth: '400px' }}>
            <FormControl fullWidth>
              <InputLabel
                size="small"
                id="bid-firm"
                sx={{
                  fontSize: 'var(--text-sm)',
                  fontFamily: 'var(--font-data)',
                  color: 'var(--text-secondary)',
                  '&.Mui-focused': {
                    color: 'var(--text-primary)',
                  },
                }}
              >
                BID Firm
              </InputLabel>
              <Select
                size="small"
                labelId="bid-firm"
                id="bid-firm"
                value={platform || ''}
                label="BID Firm"
                onChange={handleChangePlatform}
                sx={{
                  bgcolor: 'var(--surface-base)',
                  borderRadius: 0,
                  border: '1px solid var(--border-subtle)',
                  boxShadow: 'none',
                  fontFamily: 'var(--font-data)',
                  fontSize: 'var(--text-sm)',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover': {
                    borderColor: 'var(--border-default)',
                  },
                  '&.Mui-focused': {
                    outline: 'var(--focus-ring)',
                    outlineOffset: 0,
                    borderColor: 'transparent',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  },
                  '& .MuiSelect-select': {
                    fontSize: 'var(--text-sm)',
                    fontFamily: 'var(--font-data)',
                    color: 'var(--text-primary)',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: 'var(--surface-base)',
                      borderRadius: 0,
                      boxShadow: 'none',
                      border: '1px solid var(--border-default)',
                      mt: 0,
                      '& .MuiMenuItem-root': {
                        fontSize: 'var(--text-sm)',
                        fontFamily: 'var(--font-data)',
                        py: 1,
                        px: 2,
                        color: 'var(--text-primary)',
                        backgroundColor: 'var(--surface-base)',
                        '&:hover': {
                          backgroundColor: 'var(--surface-raised)',
                          color: 'var(--text-primary)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'var(--surface-overlay)',
                          color: 'var(--text-primary)',
                          '&:hover': {
                            backgroundColor: 'var(--surface-raised)',
                            color: 'var(--text-primary)',
                          },
                        },
                      },
                    },
                  },
                }}
              >
                {getBuyAndBuildPlatformsNode?.map(node => (
                  <MenuItem key={node?.dbId} value={node?.dbId}>
                    {node?.shortName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
        {activeTab === TabIndex.VALIDATION ? (
          validationInboxLoading ? (
            <Box display="flex" alignItems="center" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <CustomPaper sx={{ flexDirection: 'column', p: 0, overflow: 'hidden', width: '100%' }}>
              <DataGrid
                paginationMode="client"
                loading={validationInboxLoading}
                disableRowSelectionOnClick
                rows={validationInboxData?.validationInbox || []}
                getRowId={row => row.companyId}
                columns={validationInboxColumns}
                rowHeight={42}
                autoHeight
                getRowClassName={params => {
                  const lockedBy = params.row?.validationLockedBy;
                  return lockedBy ? 'validation-row-locked' : '';
                }}
                sx={{
                  width: '100%',
                  minWidth: 0,
                  fontFamily: 'var(--font-data)',
                  fontSize: 'var(--text-sm)',
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid var(--border-subtle)',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 12px',
                    color: 'var(--text-primary)',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    borderBottom: '1px solid var(--border-default)',
                    backgroundColor: 'var(--surface-inset)',
                    minHeight: '48px !important',
                    maxHeight: '48px !important',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 600,
                    fontSize: 'var(--text-sm)',
                    fontFamily: 'var(--font-data)',
                    color: 'var(--text-primary)',
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': { backgroundColor: 'var(--surface-overlay)' },
                  },
                  '& .validation-row-locked': {
                    opacity: 0.5,
                    pointerEvents: 'none',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: '1px solid var(--border-default)',
                  },
                }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                pageSizeOptions={[10, 25, 50, 100]}
              />
            </CustomPaper>
          )
        ) : activeInboxLoading ? (
          <Box display="flex" alignItems="center" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <CustomPaper
            sx={{
              flexDirection: 'column',
              p: 0,
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <DataGrid
              paginationMode="client"
              loading={activeInboxLoading}
              disableRowSelectionOnClick
              rows={activeInboxData || []}
              getRowId={row => row.companyId ?? row.dbId}
              columns={productCategoryColumns}
              rowHeight={42}
              autoHeight
              sx={{
                width: '100%',
                minWidth: 0,
                fontFamily: 'var(--font-data)',
                fontSize: 'var(--text-sm)',
                '& .MuiDataGrid-root': {
                  border: 'none',
                },
                '& .MuiDataGrid-main': {
                  overflow: 'visible',
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid var(--border-subtle)',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 12px',
                  color: 'var(--text-primary)',
                },
                '& .MuiDataGrid-columnHeaders': {
                  borderBottom: '1px solid var(--border-default)',
                  backgroundColor: 'var(--surface-inset)',
                  minHeight: '48px !important',
                  maxHeight: '48px !important',
                },
                '& .MuiDataGrid-columnHeader': {
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 12px',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 600,
                  fontSize: 'var(--text-sm)',
                  fontFamily: 'var(--font-data)',
                  color: 'var(--text-primary)',
                },
                '& .MuiDataGrid-virtualScroller': {
                  minHeight: '400px',
                  overflow: 'auto !important',
                },
                '& .MuiDataGrid-virtualScrollerContent': {
                  width: 'fit-content',
                  minWidth: '100%',
                },
                '& .MuiDataGrid-columnHeadersInner': {
                  minWidth: 'fit-content',
                },
                '& .MuiDataGrid-row': {
                  minWidth: 'fit-content',
                  '&:hover': {
                    backgroundColor: 'var(--surface-overlay)',
                  },
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '1px solid var(--border-default)',
                },
              }}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    topRelatedCompanyName: !isActiveFalseNegatives,
                    productCategorization: !isActiveFalseNegatives,
                    productName: useNewInbox,
                    customerVertical: useNewInbox,
                    callabilityScore: useNewInbox,
                  },
                },
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                  },
                },
              }}
              pageSizeOptions={[10, 25, 50]}
            />
          </CustomPaper>
        )}
        {companyParamId && (
          <Box>
            <Button
              disabled={!companyParamId || shortCompanyLoading}
              variant="contained"
              onClick={handleMoveToValidation}
            >
              {t('validation.moveToValidateProcess', { companyName: companyName })}
            </Button>
          </Box>
        )}
        <CustomBackdrop open={markCompanyAsNotSoftwareLoading || startCrawlLoading} />

        {/* URL Preview Popover */}
        <Popover
          open={isPreviewOpen}
          anchorEl={previewAnchor}
          onClose={handlePreviewClose}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          disableRestoreFocus
          sx={{
            pointerEvents: 'none',
          }}
          PaperProps={{
            onMouseEnter: () => {
              // Keep popover open when mouse enters it
              if (hoverTimeout) {
                clearTimeout(hoverTimeout);
                setHoverTimeout(null);
              }
            },
            onMouseLeave: handlePreviewClose,
            sx: {
              pointerEvents: 'auto',
              width: '600px',
              height: '400px',
              overflow: 'hidden',
            },
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              position: 'relative',
            }}
          >
            {previewUrl ? (
              <iframe
                src={previewUrl}
                title="Company website preview"
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                sandbox="allow-scripts allow-same-origin"
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </Box>
        </Popover>
      </Box>
    </Box>
  );
};

export default CrawlerInbox;
